import { LitElement, css, html } from "lit";
import type { TemplateResult } from "lit";
import { sharedStyles } from "../styles.js";

/**
 * A feed's favicon, or a letter avatar when none is stored. `url` is the
 * same-origin favicon path (null renders the avatar); `label` supplies the
 * avatar letter (first character, uppercased).
 *
 * Size is controlled by the `--nf-favicon-size` custom property (default 16px).
 */
export class NfFavicon extends LitElement {
  static override properties = {
    url: { type: String },
    label: { type: String },
  };

  url: string | null = null;
  label = "";

  static override styles = [
    sharedStyles,
    css`
      :host {
        display: inline-block;
      }
      .favicon {
        width: var(--nf-favicon-size, 16px);
        height: var(--nf-favicon-size, 16px);
        border-radius: 4px;
        overflow: hidden;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        flex-shrink: 0;
        vertical-align: middle;
        background: var(--nf-accent);
        color: var(--nf-accent-fg);
        font-size: calc(var(--nf-favicon-size, 16px) * 0.6);
        font-weight: 700;
        line-height: 1;
      }
      img {
        width: 100%;
        height: 100%;
        object-fit: cover;
        display: block;
      }
    `,
  ];

  private letter(): string {
    const ch = this.label.trim().charAt(0);
    return ch ? ch.toUpperCase() : "?";
  }

  override render(): TemplateResult {
    return html`<span class="favicon" aria-hidden="true"
      >${
        this.url
          ? html`<img src=${this.url} alt="" loading="lazy" />`
          : this.letter()
      }</span
    >`;
  }
}

customElements.define("nf-favicon", NfFavicon);

declare global {
  interface HTMLElementTagNameMap {
    "nf-favicon": NfFavicon;
  }
}
